import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { faCopy, faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import TeamsLogo from '/images/teams.svg';
import { Alert } from '../../../core/components/Alert.tsx';
import Button from '../../../core/components/Button.tsx';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { parseRedirectUrl } from '../../../core/utils/link.ts';
import { ConnectionAction, ConnectionStatus } from '../../../document/constants.ts';
import {
    CONNECT_TEAMS_INTEGRATION,
    DELETE_TEAMS_INTEGRATION,
    GET_TEAMS_ADMIN_CONSENT_URL,
    GET_TEAMS_INTEGRATION,
} from '../queries.ts';

const DotIcon = withIcon(faCircle);
const CopyIcon = withIcon(faCopy);
const ExternalLinkIcon = withIcon(faExternalLink);

const ConnectButton = ({ label, disabled, onClick }) => (
    <Button
        variant="ghost"
        onClick={onClick}
        disabled={disabled}
        className="flex items-center h-8 font-medium text-sm px-2 border border-line-weak"
    >
        {label}
        <ExternalLinkIcon className="pl-2" />
    </Button>
);

const isAllowedOrigin = (url: URL) =>
    url.origin === window.location.origin || url.hostname === 'login.microsoftonline.com';

const TeamsIntegrationCard = () => {
    const { t } = useTranslation('voice_agents');
    const { publishToast } = useToaster();

    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [adminConsentLink, setAdminConsentLink] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();

    const { data, refetch } = useQuery(GET_TEAMS_INTEGRATION, { fetchPolicy: 'network-only' });
    const [connectTeams, { loading: connectLoading }] = useMutation(CONNECT_TEAMS_INTEGRATION);
    const [getAdminConsentUrl] = useMutation(GET_TEAMS_ADMIN_CONSENT_URL);
    const [deleteTeamsIntegration, { loading: deleteLoading }] = useMutation(DELETE_TEAMS_INTEGRATION);

    const integration = data?.teamsIntegration;
    const isConnected = Boolean(integration?.isConnected);
    const isAdminConsentGranted = Boolean(integration?.isAdminConsentGranted);
    // Posting call summaries into a Teams channel needs permissions that were added after the first accounts were
    // connected — those accounts have to consent again and reconnect.
    const needsChannelPermissions = isConnected && !integration?.supportsChannelMessages;
    const needsAdminConsent = needsChannelPermissions || (!isConnected && !isAdminConsentGranted);

    // The Teams OAuth callback redirects back to this page with ?teamsIntegration=success|error
    useEffect(() => {
        const status = searchParams.get('teamsIntegration');
        if (status === 'success') {
            publishToast({ description: t('teamsIntegration.connectSuccess'), status: 'success' });
        } else if (status === 'error') {
            publishToast({ description: t('teamsIntegration.connectError'), status: 'error' });
        }
        if (status) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete('teamsIntegration');
            setSearchParams(nextParams, { replace: true });
        }
    }, []);

    useEffect(() => {
        const fetchAdminConsentUrl = async () => {
            if (!needsAdminConsent) return;
            try {
                const res = await getAdminConsentUrl();
                const adminConsentUrl = res.data?.getTeamsAdminConsentUrl?.adminConsentUrl;
                if (adminConsentUrl) setAdminConsentLink(adminConsentUrl);
            } catch (error) {
                console.error('Failed to fetch the Teams admin consent URL:', error);
            }
        };

        fetchAdminConsentUrl();
    }, [needsAdminConsent, getAdminConsentUrl]);

    const handleConnect = async () => {
        try {
            const res = await connectTeams();
            const redirectUrl = res.data?.connectTeamsIntegration?.redirectUrl;
            if (!redirectUrl) return;
            const url = parseRedirectUrl(redirectUrl);
            if (isAllowedOrigin(url)) {
                window.location.href = url.toString();
            } else {
                console.error('Blocked unsafe redirect:', redirectUrl);
                publishToast({ description: t('teamsIntegration.connectError'), status: 'error' });
            }
        } catch (error) {
            console.error(error);
            publishToast({ description: t('teamsIntegration.connectError'), status: 'error' });
        }
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(adminConsentLink);
            publishToast({ description: t('teamsIntegration.linkCopied'), status: 'success' });
        } catch (error) {
            console.error('Failed to copy link:', error);
            publishToast({ description: t('teamsIntegration.linkCopyFailed'), status: 'error' });
        }
    };

    const handleDisconnect = async () => {
        try {
            await deleteTeamsIntegration();
            publishToast({ description: t('teamsIntegration.disconnectSuccess'), status: 'success' });
            await refetch();
        } catch (error) {
            console.error(error);
            publishToast({ description: t('teamsIntegration.disconnectError'), status: 'error' });
        }
        setIsDisconnecting(false);
    };

    const handleStatusChange = (action: string) => {
        if (action === ConnectionAction.Disconnect) {
            setIsDisconnecting(true);
        }
    };

    return (
        <section className="w-full max-w-[48rem] flex flex-col border border-solid border-line-weak bg-surface-weak rounded-lg p-8 gap-6">
            <div className="w-full flex flex-col gap-4 md:flex-row md:gap-0">
                <div className="rounded-lg border border-solid border-line-weak h-12 w-12 p-[7px] flex items-center">
                    <img src={TeamsLogo} alt="Microsoft Teams logo" />
                </div>

                <div className="flex-2 md:ml-3 flex flex-col md:self-center">
                    <div>
                        <span className="font-semibold text-sm text-content-strong">{t('teamsIntegration.name')}</span>
                    </div>
                    <span className="font-regular text-sm text-content-medium">
                        {t('teamsIntegration.description')}
                    </span>
                </div>

                <div className="md:self-center">
                    {isConnected ? (
                        <SelectField
                            value={ConnectionStatus.Connected}
                            options={[{ label: ConnectionAction.Disconnect, value: ConnectionAction.Disconnect }]}
                            required
                            isLoading={deleteLoading}
                            className="bg-surface-brand-medium-default"
                            renderValue={() => (
                                <span className="text-content-brand-medium font-medium text-sm flex items-center">
                                    <DotIcon className="text-[10px] pr-2 pb-[1px] text-content-success-weak" />
                                    {t('teamsIntegration.connected')}
                                </span>
                            )}
                            renderOption={(option) => (
                                <span className="text-content-strong">
                                    {t(`teamsIntegration.${option.label.toLowerCase()}`)}
                                </span>
                            )}
                            onValueChange={handleStatusChange}
                        />
                    ) : (
                        <ConnectButton
                            label={t('teamsIntegration.connectButton')}
                            disabled={connectLoading || !isAdminConsentGranted}
                            onClick={handleConnect}
                        />
                    )}
                </div>
            </div>

            {isConnected && (
                <div className="w-full">
                    <span className="font-semibold text-sm pt-2 text-content-strong">
                        {t('teamsIntegration.connectedAccount')}
                    </span>
                    <span className="block pt-2 text-sm text-content-strong">{integration?.accountEmail}</span>
                </div>
            )}

            {needsChannelPermissions && (
                <Alert severity="warning" className="w-full max-w-[520px]">
                    <div className="flex flex-col items-start gap-2">
                        {t('teamsIntegration.channelPermissionsMissing')}
                        <ConnectButton
                            label={t('teamsIntegration.reconnectButton')}
                            disabled={connectLoading}
                            onClick={handleConnect}
                        />
                    </div>
                </Alert>
            )}

            {needsAdminConsent && adminConsentLink && (
                <div className="w-full max-w-[520px]">
                    <span className="mt-4 font-regular text-sm text-content-medium">
                        {t('teamsIntegration.adminConsentInstruction')}
                    </span>
                    <div className="mt-4 justify-between flex items-center gap-2 bg-surface-medium p-2 rounded-sm">
                        <span className="font-regular text-sm truncate max-w-[400px] text-content-strong">
                            {adminConsentLink}
                        </span>
                        <Button
                            variant="ghost"
                            onClick={handleCopyLink}
                            className="flex items-center gap-1 px-2 py-1 h-auto text-xs cursor-pointer"
                        >
                            <CopyIcon className="text-sm hover:bg-transparent" />
                            {t('teamsIntegration.copyLink')}
                        </Button>
                    </div>
                </div>
            )}

            {isDisconnecting && (
                <ConfirmModal
                    onConfirm={handleDisconnect}
                    onCancel={() => setIsDisconnecting(false)}
                    title={t('teamsIntegration.disconnectConfirmTitle')}
                    labelConfirm={t('teamsIntegration.disconnectButton')}
                    confirmDisabled={deleteLoading}
                    danger
                >
                    {t('teamsIntegration.disconnectConfirmMessage')}
                </ConfirmModal>
            )}
        </section>
    );
};

export default TeamsIntegrationCard;
